// controls/checkbox.js — version refactorisée avec OptionsEditor
import React, { useState, useEffect } from "react";
import CommonControls from "./inc/Common";
import OptionsEditor from "./inc/OptionsEditor";

/**
 * Création d’un champ "checkbox group" conforme JSON Schema
 */
export function createCheckboxField() {
  return {
    type: "array",
    title: "Cases à cocher",
    "x-ui": "checkbox",
    items: {
      type: "string",
      enum: [""],
    },
    uniqueItems: true,
    "x-options": [{ text: "Option 1", value: "option_1" }],
    minItems: 0,
    maxItems: 0, // 0 = pas de limite
  };
}

/**
 * Éditeur du champ checkbox group
 */
export function EditCheckboxField({ field, onChange }) {
  const [min, setMin] = useState(field.minItems ?? 0);
  const [max, setMax] = useState(field.maxItems ?? 0);

  const handleOptionsChange = (options) => {
    const updatedField = {
      ...field,
      "x-options": options,
      items: { type: "string", enum: options.map((o) => o.value) },
      minItems: min,
      maxItems: max,
    };
    onChange(updatedField);
  };

  // 🔄 synchro vers parent si min/max changent
  useEffect(() => {
    handleOptionsChange(field["x-options"] || []);
  }, [min, max]);

  return (
    <>
      <CommonControls field={field} onChange={onChange} />

      <OptionsEditor
        initialOptions={field["x-options"]}
        onChange={handleOptionsChange}
        label="Cases à cocher"
      />

      {/* ---- Contraintes ---- */}
      <div className="mb-3">
        <label className="form-label fw-bold">Contraintes de sélection</label>
        <div className="d-flex gap-2">
          <div>
            <label className="form-label">Minimum cochées</label>
            <input
              type="number"
              className="form-control form-control-sm"
              min="0"
              value={min}
              onChange={(e) => setMin(Number(e.target.value))}
            />
          </div>
          <div>
            <label className="form-label">Maximum cochées</label>
            <input
              type="number"
              className="form-control form-control-sm"
              min="0"
              value={max}
              onChange={(e) => setMax(Number(e.target.value))}
            />
          </div>
        </div>
        <small className="text-muted">
          0 = pas de limite. Les contraintes sont appliquées à la validation.
        </small>
      </div>
    </>
  );
}

export const editCheckboxField = (props) => <EditCheckboxField {...props} />;

/* =========================================================
   🧩 FRONT — rendu du champ cases à cocher (checkbox group)
   ========================================================= */
export const RenderCheckboxField = ({
  field,
  value,
  onChange,
  className,
  isReadOnly,
  errors   // valeur string éventuelle : "Champ requis"
}) => {

  const options = field["x-options"] || [];
  const isRequired =
    field["x-effective-required"] === true ||
    field["x-required"] === true;
  const minItems = field.minItems ?? 0;
  const maxItems = field.maxItems ?? 0;

  // Valeur normalisée (toujours un tableau)
  const selected = Array.isArray(value) ? value : [];

  const handleToggle = (val) => {
    let updated;
    if (selected.includes(val)) {
      updated = selected.filter((v) => v !== val);
    } else {
      updated = [...selected, val];
    }

    // Gestion des contraintes
    if (maxItems > 0 && updated.length > maxItems) return;
    onChange?.(updated);
  };

  return (
    <div
      className={`
        fb-field fb-checkbox 
        ${field["x-bloc-class"] || ""} 
        ${errors ? "fb-error-field" : ""} 
        ${className || ""}
        ${isRequired ? "fb-required" : ""}
      `}
    >

      {field.title && (
        <label
          className={`form-label d-block`}
        >
          {field.title}
        </label>
      )}

      {/* Groupe de cases */}
      <div className={`d-flex flex-column gap-1 ${field["x-control-class"] || ""}`}>
        {options.map((opt, i) => (
          <div key={i} className="form-check">
            <input
              className={`form-check-input`}
              type="checkbox"
              id={`${field.name || field.id}_${i}`}
              value={opt.value}
              checked={selected.includes(opt.value)}
              onChange={() => handleToggle(opt.value)}
              readOnly={isReadOnly}
              disabled={isReadOnly}
            />
            <label
              className={`form-check-label ${errors ? "text-danger" : ""}`}
              htmlFor={`${field.name || field.id}_${i}`}
            >
              {opt.text}
            </label>
          </div>
        ))}
      </div>

      {/* Aide max items */}
      {maxItems > 0 && (
        <small className="text-muted">
          Vous pouvez cocher jusqu’à {maxItems} option{maxItems > 1 ? "s" : ""}.
        </small>
      )}

      {/* Message d'erreur */}
      {/* errorMessage && (
        <div className="invalid-feedback d-block mt-1">
          {errorMessage}
        </div>
      ) */}
    </div>
  );
};
